import { BadStatusError } from '@confluence/network';

import type { DependencyOperation } from '../../dependencyHealthAnalytics.ts';
import {
	useDependencyReadHealth,
	type DependencyRead,
} from '../../hooks/useDependencyReadHealth.ts';

const TRAIT_OPERATIONS = new Map<string, DependencyOperation>([
	['rovo_connector_authed_users_percentage_by_app', 'connectorAuthRates'],
	['confluence_3p_smartlink_connection_link_count', 'smartLinkCounts'],
	['confluence_smartlink_connector_adoption_by_app', 'connectedAppsRates'],
]);

export type TapTraitHealthOptions = {
	traitName: string;
	skip: boolean | undefined;
	loading: boolean;
	hasData: boolean;
	error: Error | null;
	result: unknown;
};

const traitRead = (
	{ skip, loading, hasData, error, result }: TapTraitHealthOptions,
	operation: DependencyOperation | undefined,
): DependencyRead => {
	if (skip || !operation) {
		return { status: 'idle' };
	}
	if (loading) {
		return { status: 'loading' };
	}
	if (error) {
		if (result !== undefined) {
			return { status: 'settled', outcome: 'partial', result, error };
		}
		const denied = BadStatusError.isBadStatusError(error) && error.response.status === 403;
		return { status: 'settled', outcome: denied ? 'denied' : 'failed', result, error };
	}
	// The shared store starts idle before its bootstrap effect starts a request.
	if (!hasData) {
		return { status: 'idle' };
	}
	return { status: 'settled', outcome: result === undefined ? 'unavailable' : 'succeeded', result };
};

export const useTapTraitHealth = (options: TapTraitHealthOptions): void => {
	const operation = TRAIT_OPERATIONS.get(options.traitName);
	useDependencyReadHealth({
		dependency: 'siteTraits',
		operation: operation ?? 'read',
		identity: options.traitName,
		read: traitRead(options, operation),
	});
};
